import React, { useMemo, useEffect, useRef } from 'react';
import useReactRouter from 'use-react-router';
import window from 'window';
import qs from 'query-string';

import log from 'loglevel';

import { Switch, Route } from 'react-router-dom';
import Logo from 'features/Logo/Logo';

import Home from 'features/Home';

import OfferIntro, { OfferIntroError } from 'features/unified/OfferIntro';
import UnifiedOtpVerification from 'features/unified/ConsumerSearch/OtpVerification';
import CardChoice from 'features/CardChoice';
import CardSelectorRoute from 'routes/CardSelectorRoute';
import CardSelector from 'features/CardSelector/CardSelector';
import MagicLinkVerification from 'features/magicLink/MagicLinkVerification';
import ConsumerSearch from 'features/ConsumerSearch';
import EnhancedCheckoutConsumerSearch, {
  EnhancedCheckoutError,
} from 'features/enhancedCheckout/ConsumerSearch';
import CreditApplication from 'features/CreditApplication';
import MembershipHome from 'features/MembershipValidationHome';
import MembershipValidation from 'features/MembershipValidation';
import LoyaltyNumberError from 'features/Home/LoyaltyNumberError';
import ClerkMode from 'features/ClerkMode';
import AddOns from 'features/AddOns';
import ReviewTerms from 'features/ReviewTerms';
import AppExpired from 'features/AppExpired';
import DspExpired from 'features/DspExpired';
import PrescreenOffer from 'features/prescreen/PrescreenOffer';
import BatchPrescreenStart from 'features/prescreen/BatchPrescreenStart';
import BatchPrescreenExpired from 'features/prescreen/BatchPrescreenStart/Expired';
import BatchPrescreenError from 'features/prescreen/BatchPrescreenStart/Error';
import FallbackReviewTerms from 'features/prescreen/FallbackReviewTerms';
import FallbackApplication from 'features/prescreen/FallbackApplication';
import PrequalFallbackApplication from 'features/prequal/FallbackApplication';
import PrequalConsumerSearch, {
  AdditionalVerification,
} from 'features/prequal/ConsumerSearch';
import PrequalConsumerSearchHome from 'features/prequal/ConsumerSearchHome';
import PrequalDecline from 'features/prequal/Decline';
import PrequalOffer from 'features/prequal/PrequalOffer';
import PrequalOfferPush from 'features/prequal/PrequalOfferPush';
import PrequalOfferError from 'features/prequal/PrequalOfferPush/Error';
import PrequalOfferExpired from 'features/prequal/PrequalOfferPush/Expired';
import PrequalOfferApplication, {
  PrequalOfferApplicationError,
  PrequalOfferApplicationPending,
  PrequalOfferApplicationNotQualified,
  PrequalOfferApplicationExistingPreapproval,
} from 'features/prequal/PrequalOfferApplication';
import PrequalOfferPushApplication from 'features/prequal/PrequalOfferPushApplication';
import PrequalFallbackReviewTerms from 'features/prequal/FallbackReviewTerms';
import DriverLicenseStart from 'features/DriverLicense/DriverLicenseStart';
import DriverLicenseHome from 'features/DriverLicense/DriverLicenseHome';
import DriverLicenseError from 'features/DriverLicense/DriverLicenseError';
import ScanPage from 'features/DriverLicense/ScanPage';
import PrequalDriverLicenseHome from 'features/prequal/PrequalDriverLicenseHome';
import PrequalDriverLicenseStart from 'features/prequal/PrequalDriverLicenseStart';
import AddToMerchants from 'features/AddToMerchants/AddToMerchants';
import AccountSelection from 'features/AccountSelection/AccountSelection';

import CreditPending from 'features/CreditPending';
import ExistingAccount from 'features/ExistingAccount';
import ExistingAccountValidationError from 'features/ExistingAccount/ValidationError';
import CreditError from 'features/CreditError';
import DspStart from 'features/DspStart';
import DspError from 'features/DspError';
import DspRetrieval from 'features/DspRetrieval';
import ThemeProvider from 'features/Theme';
import SnackbarProvider from 'components/SnackbarProvider';

import AppTimeoutModal from 'features/TimeoutModal/AppTimeoutModal';
import DspTimeoutModal from 'features/TimeoutModal/DspTimeoutModal';
import ResetApplicationRoute from 'features/ResetApplicationRoute';
import WrappedRoute from 'routes/WrappedRoute';
import MultipleAccountRoute from 'routes/MultipleAccountRoute';

import {
  EntryTokenProvider,
  EntryRoute,
  NonEntryRoute,
  NoReturnRoute,
} from 'routes/EntryProtectedRoute';

import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectTenant } from 'state/tenant';
import { IovationProvider } from 'hooks/useIovation';
import useFeature from 'hooks/useFeature';
import { TrackedRoute } from 'hooks/useAnalytics';
import useFmcHost, { FmcHostProvider } from 'hooks/useFmcHost';
import useFieldCustomization from 'hooks/useFieldCustomization';

import Redirect from 'components/Redirect';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import nest from 'utils/nest';
import UnloadPrompt from 'components/UnloadPrompt';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import { mergeApplication } from 'state/creditApplication';
import { loadAdobeTrackingId, heightChanged } from 'fmc-sdk';
import useHeightObserver from 'hooks/useHeightObserver';
import Approval from 'features/Approval';
import CheckoutApproval from 'features/Approval/CheckoutApproval';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import canUseDOM from 'utils/canUseDOM';
import ApplicationRoute from 'routes/ApplicationRoute';
import {
  BackButtonContextProvider,
  BackButtonContainer,
} from 'features/BackButton';
import { ResponsiveQueryProvider } from 'hooks/useResponsiveQuery';
import { IdiSdkProvider } from 'hooks/useIdiSdk';
import EmailVerification from 'features/unified/ConsumerSearch/EmailVerification';
import MobileVerification from 'features/unified/ConsumerSearch/MobileVerification';
import SplitPayLearnMore from 'features/unified/Offer/SplitPayLearnMore';
import CheckoutSplitPayLearnMore from 'features/unified/Offer/CheckoutOffer/CheckoutSplitPayLearnMore';
import CreditCardOfferLearnMore from 'features/unified/CreditCardOfferLearnMore';
import UnifiedPrequalOffer from 'features/unified/Offer';
import BillingAddress from 'features/unified/Application/BillingAddress';
import Eligibility from 'features/unified/Application/Eligibility';
import UnifiedApplicationError from 'features/unified/Application/Error';
import UnifiedApplicationNotQualified from 'features/unified/Application/NotQualified';
import UnifiedApplicationPending from 'features/unified/Application/Pending';
import InstallmentsLearnMore from 'features/unified/Offer/InstallmentsLearnMore';
import ProductChoice from 'features/ProductChoice';
import UnifiedCreditApplicationReview from 'features/unified/OfferReview/UnifiedCreditApplicationReview';
import UnifiedPrequalApplicationReview from 'features/unified/OfferReview/UnifiedPrequalApplicationReview';
import InstallmentsPlaceOrder from 'features/unified/PlaceOrder/InstallmentsPlaceOrder';
import SplitPayPlaceOrder from 'features/unified/PlaceOrder/SplitPayPlaceOrder';
import NonEmbeddedSuccess from 'features/unified/PlaceOrder/NonEmbeddedSuccess';
import CheckoutOfferIntro from 'features/unified/CheckoutOfferIntro/CheckoutOfferIntro';
import UnifiedCheckoutOffer from 'features/unified/Offer/CheckoutOffer/UnifiedCheckoutOffer';
import UnifiedPrequalOfferPushApplication from 'features/unified/UnifiedPrequalOfferPushApplication/UnifiedPrequalOfferPushApplication';
import UnifiedCheckoutHome, {
  UnifiedCheckoutHomeError,
} from 'features/unified/Home/UnifiedCheckoutHome';
import UnifiedPrequalHome from 'features/unified/Home/UnifiedPrequalHome';
import UnifiedFraudAlert from 'features/unified/PlaceOrder/Error/FraudAlert';
import UnifiedFraudAlertOfac from 'features/unified/PlaceOrder/Error/FraudAlertOfac';
import UnifiedPending from 'features/unified/PlaceOrder/Error/Pending';
import useLoadInitialData from 'hooks/useLoadInitialData';
import { IssuanceLoadingPage } from 'features/unified/Issuance/IssuanceLoadingPage';
import UnifiedIssuanceErrorAlert from 'features/unified/Issuance/IssuanceError';
import TextMeMyOfferDeadend from 'features/unified/Offer/TextMeMyOfferDeadend';
import UnifiedIssuanceTimeout from 'features/unified/Issuance/IssuanceTimeout';
import Acceptance from 'features/magicLink/Acceptance/Acceptance';
import AcceptanceError from 'features/magicLink/AcceptanceError/AcceptanceError';

// import Footer from 'components/Footer';

const App: React.FC = () => {
  const initialDataError = useLoadInitialData();

  const { i18n } = useTranslation();
  const {
    stringsFileName,
    overrideConfig: { clerkMode },
  } = useSelector(selectTenant);

  const { isEnabled } = useFeature();
  const { hasStep } = useWorkflow();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();

  const { isRequired, isVisible } = useFieldCustomization();

  const { observeHeight } = useHeightObserver();

  const { location } = useReactRouter();

  const { associateId, loyaltyNumber } = useMemo(() => {
    return qs.parse(location.search, {
      parseBooleans: true,
    });
  }, [location.search]);

  const hasClerkMode = useMemo(() => {
    return clerkMode && !associateId && isEnabled('clerkMode');
  }, [associateId, clerkMode, isEnabled]);

  useMemo(() => {
    i18n.setDefaultNamespace(stringsFileName || 'default');
  }, [i18n, stringsFileName]);

  const { setAnalyticsVariablesOnLoad } = useAnalyticsVariables();

  const analyticsVariableSet = useRef<boolean>(false);
  if (!analyticsVariableSet.current && canUseDOM) {
    // We do this once on the first render on the client side to make sure it is
    // earlier than other tracking calls.
    setAnalyticsVariablesOnLoad();
    analyticsVariableSet.current = true;
  }

  useEffect(() => {
    if ('_satellite' in window) {
      let mcid: null | string = null;
      // We'll swallow all errors from the analytics suite and log it instead
      // to prevent it from breaking the app.
      try {
        mcid = window._satellite.getVisitorId().getMarketingCloudVisitorID();
      } catch (e) {
        log.error(e);
      }
      if (mcid) {
        fmcDispatch(loadAdobeTrackingId(mcid));
        dispatch(mergeApplication({ mcid }));
      }
    } else {
      log.warn('Adobe Satellite not loaded');
    }
  }, [dispatch, fmcDispatch]);

  useEffect(() => {
    const onHeightChange = (height: number) => {
      fmcDispatch(heightChanged(height));
    };

    return observeHeight(document.body, onHeightChange);
  }, [observeHeight, fmcDispatch]);

  const hasLoyaltyNumberError = useMemo(() => {
    return (
      isRequired('loyaltyNumber') &&
      !isVisible('loyaltyNumber') &&
      !loyaltyNumber
    );
  }, [isRequired, isVisible, loyaltyNumber]);

  return (
    <div role="main">
      <BackButtonContainer />
      <Logo />

      <ErrorBoundary>
        <Switch>
          <WrappedRoute
            path="/loyalty-number-error"
            wrappers={[TrackedRoute]}
            component={LoyaltyNumberError}
          />
          {hasLoyaltyNumberError && <Redirect to="/loyalty-number-error" />}
          {initialDataError === 'unifiedInitialStateError' && (
            <Redirect to="/unified/checkout/lookup-error" />
          )}

          {hasClerkMode && (
            <WrappedRoute
              exact
              path="/associate-details"
              wrappers={[EntryRoute, TrackedRoute]}
              component={ClerkMode}
            />
          )}

          {hasClerkMode && !canUseDOM && <Redirect to="/associate-details" />}

          {hasStep(Step.PRODUCT_CHOICE) ? (
            <WrappedRoute
              path="/product-choice"
              wrappers={[
                TrackedRoute,
                NonEntryRoute,
                ApplicationRoute,
                CardSelectorRoute,
              ]}
              component={ProductChoice}
            />
          ) : null}

          {hasStep(Step.CARD_CHOICE) ? (
            <WrappedRoute
              path="/card-choice"
              wrappers={[
                TrackedRoute,
                NonEntryRoute,
                CardSelectorRoute,
                ApplicationRoute,
              ]}
              component={CardChoice}
            />
          ) : null}

          {hasStep(Step.CARD_SELECTOR) ? (
            <WrappedRoute
              path="/card-selector"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={CardSelector}
            />
          ) : null}

          {hasStep(Step.SECOND_CARD_SELECTOR) ? (
            <WrappedRoute
              path="/card-selector-fallback"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={CardSelector}
            />
          ) : null}

          {hasStep(Step.CONSUMER_SEARCH) ? (
            <WrappedRoute
              path="/consumer-search"
              wrappers={[
                TrackedRoute,
                NonEntryRoute,
                CardSelectorRoute,
                ApplicationRoute,
              ]}
              component={ConsumerSearch}
            />
          ) : null}

          {hasStep(Step.MAGIC_LINK_VERIFICATION) ? (
            <WrappedRoute
              path="/magic-link-verification"
              wrappers={[
                TrackedRoute,
                NonEntryRoute,
                CardSelectorRoute,
                ApplicationRoute,
              ]}
              component={MagicLinkVerification}
            />
          ) : null}

          <WrappedRoute
            // This SHOULD NOT be an exact match because it renders
            // additional nested routes. If it's an exact match, then
            // /credit-application/<anything> won't be matched.
            path="/credit-application"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={CreditApplication}
          />

          {hasStep(Step.ADD_ONS) ? (
            <WrappedRoute
              path="/add-ons"
              wrappers={[
                TrackedRoute,
                NonEntryRoute,
                CardSelectorRoute,
                ApplicationRoute,
              ]}
              component={AddOns}
            />
          ) : null}

          <WrappedRoute
            path="/review-terms"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={ReviewTerms}
          />

          <WrappedRoute
            path="/add-merchants"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={AddToMerchants}
          />

          {/** DSP PUSH */}

          {isEnabled('dspPush') ? (
            <WrappedRoute
              path="/dsp-start"
              wrappers={[TrackedRoute, EntryRoute, AppTimeoutModal]}
              component={DspStart}
            />
          ) : null}

          {isEnabled('dspRetrieval') ? (
            <WrappedRoute
              path="/dsp-retrieval"
              wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
              component={DspRetrieval}
            />
          ) : null}

          {/** PRESCREEN */}

          <WrappedRoute
            exact
            path="/prescreen/offer"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrescreenOffer}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/start"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={BatchPrescreenStart}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/expired"
            wrappers={[TrackedRoute, ApplicationRoute]}
            component={BatchPrescreenExpired}
          />

          <WrappedRoute
            exact
            path="/batch-prescreen/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={BatchPrescreenError}
          />

          <WrappedRoute
            path="/prescreen/application"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={FallbackApplication}
          />

          <WrappedRoute
            path="/prescreen/review-terms"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={FallbackReviewTerms}
          />

          {/* PREQUAL */}

          <WrappedRoute
            path="/prequal/phone-lookup"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={PrequalConsumerSearchHome}
          />

          <WrappedRoute
            exact
            path="/prequal/token-lookup"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={PrequalConsumerSearchHome}
          />

          <WrappedRoute
            path="/prequal/consumer-search"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              ApplicationRoute,
              CardSelectorRoute,
            ]}
            component={PrequalConsumerSearch}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={PrequalOfferApplication}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationError}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/pending"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationPending}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/not-qualified"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationNotQualified}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-application/existing-preapproval"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferApplicationExistingPreapproval}
          />

          <WrappedRoute
            path="/prequal/offer"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={PrequalOffer}
          />

          <WrappedRoute
            path="/prequal/decline"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalDecline}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-push"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={PrequalOfferPush}
          />

          <WrappedRoute
            path="/prequal/offer-push-error"
            wrappers={[TrackedRoute, NonEntryRoute]}
            component={PrequalOfferError}
          />

          <WrappedRoute
            exact
            path="/prequal/offer-push-expired"
            wrappers={[TrackedRoute, NoReturnRoute]}
            component={PrequalOfferExpired}
          />

          <WrappedRoute
            path="/prequal/offer-push-application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={PrequalOfferPushApplication}
          />

          <WrappedRoute
            path="/prequal/application"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={PrequalFallbackApplication}
          />

          <WrappedRoute
            path="/prequal/review-terms"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={PrequalFallbackReviewTerms}
          />

          {/** Enhanced checkout */}

          <WrappedRoute
            path="/enhanced-checkout/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={EnhancedCheckoutError}
          />

          <WrappedRoute
            path="/enhanced-checkout"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={EnhancedCheckoutConsumerSearch}
          />

          {/** Membership Validation */}

          <WrappedRoute
            exact
            path="/membership-validation"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={MembershipHome}
          />

          <WrappedRoute
            exact
            path="/membership-validation-start"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              AppTimeoutModal,
              ApplicationRoute,
              CardSelectorRoute,
            ]}
            component={MembershipValidation}
          />

          {/** Driver License Scan Route **/}

          <WrappedRoute
            exact
            path="/driver-license"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={DriverLicenseHome}
          />

          <WrappedRoute
            exact
            path="/prequal/driver-license"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={PrequalDriverLicenseHome}
          />

          <WrappedRoute
            exact
            path="/driver-license/start"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              AppTimeoutModal,
              CardSelectorRoute,
            ]}
            component={DriverLicenseStart}
          />

          <WrappedRoute
            exact
            path="/prequal/driver-license/start"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              AppTimeoutModal,
              CardSelectorRoute,
            ]}
            component={PrequalDriverLicenseStart}
          />

          <WrappedRoute
            exact
            path="/driver-license/scan-page"
            wrappers={[TrackedRoute, NonEntryRoute, AppTimeoutModal]}
            component={ScanPage}
          />

          <WrappedRoute
            exact
            path="/driver-license/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={DriverLicenseError}
          />

          {/* Unified prequal */}

          {/* This inconsistent path can be changed later, but it is too well
          integrated into ADS to change now */}
          <WrappedRoute
            exact
            path="/unified/offer-intro"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={UnifiedPrequalHome}
          />

          <WrappedRoute
            exact
            path="/unified/checkout"
            wrappers={[EntryRoute, AppTimeoutModal]}
            component={UnifiedCheckoutHome}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/lookup-error"
            wrappers={[TrackedRoute, EntryRoute]}
            component={UnifiedCheckoutHomeError}
          />

          <WrappedRoute
            exact
            path="/unified/prequal-start"
            wrappers={[TrackedRoute, NonEntryRoute]}
            component={OfferIntro}
          />

          <WrappedRoute
            exact
            path="/unified/checkout-start"
            wrappers={[NonEntryRoute, TrackedRoute, AppTimeoutModal]}
            component={CheckoutOfferIntro}
          />

          <WrappedRoute
            exact
            path="/unified/offer-intro/error"
            wrappers={[TrackedRoute, EntryRoute, ApplicationRoute]}
            component={OfferIntroError}
          />

          <WrappedRoute
            path="/unified/prequal-offer/splitpay/learn-more/:offerId"
            wrappers={[NonEntryRoute, TrackedRoute, ApplicationRoute]}
            component={SplitPayLearnMore}
          />

          <WrappedRoute
            path="/unified/checkout-offer/splitpay/learn-more/:offerId"
            wrappers={[NonEntryRoute, TrackedRoute, ApplicationRoute]}
            component={CheckoutSplitPayLearnMore}
          />

          <WrappedRoute
            exact
            path="/unified/consumer-search"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={EmailVerification}
          />

          <WrappedRoute
            exact
            path="/unified/mobile-verification"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={MobileVerification}
          />

          <WrappedRoute
            exact
            path="/unified/otp-verification"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={UnifiedOtpVerification}
          />

          <WrappedRoute
            exact
            path="/unified/offer-application"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={BillingAddress}
          />

          <WrappedRoute
            exact
            path="/unified/offer-application/eligibility"
            wrappers={[
              TrackedRoute,
              NonEntryRoute,
              CardSelectorRoute,
              ApplicationRoute,
            ]}
            component={Eligibility}
          />

          <WrappedRoute
            exact
            path="/unified/offer-application/error"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedApplicationError}
          />

          <WrappedRoute
            exact
            path="/unified/offer-application/not-qualified"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedApplicationNotQualified}
          />

          <WrappedRoute
            exact
            path="/unified/offer-application/pending"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedApplicationPending}
          />

          <WrappedRoute
            exact
            path="/unified/credit-card-offer/learn-more"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={CreditCardOfferLearnMore}
          />

          <WrappedRoute
            exact
            path="/unified/prequal-offer-push-application"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedPrequalOfferPushApplication}
          />

          <WrappedRoute
            exact
            path="/unified/credit-card-offer/credit-review"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedCreditApplicationReview}
          />

          <WrappedRoute
            exact
            path="/unified/credit-card-offer/prequal-review"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedPrequalApplicationReview}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/installments/place-order/:id"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={InstallmentsPlaceOrder}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/splitpay/place-order/:id"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={SplitPayPlaceOrder}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/success"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={NonEmbeddedSuccess}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/place-order/fraud-alert"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={UnifiedFraudAlert}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/place-order/fraud-alert-ofac"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={UnifiedFraudAlertOfac}
          />

          <WrappedRoute
            exact
            path="/unified/checkout/place-order/pending"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={UnifiedPending}
          />

          <WrappedRoute
            exact
            path="/unified/financing-card-issuance"
            wrappers={[TrackedRoute, NonEntryRoute]}
            component={IssuanceLoadingPage}
          />

          <WrappedRoute
            exact
            path="/unified/checkout-offer"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedCheckoutOffer}
          />

          <WrappedRoute
            exact
            path="/unified/prequal-offer"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={UnifiedPrequalOffer}
          />

          <WrappedRoute
            exact
            path="/unified/prequal-offer/installments/learn-more"
            wrappers={[TrackedRoute, NonEntryRoute, ApplicationRoute]}
            component={InstallmentsLearnMore}
          />

          {/** Ending routes */}

          <WrappedRoute
            path="/app-expired"
            wrappers={[TrackedRoute, ResetApplicationRoute, NoReturnRoute]}
            component={AppExpired}
          />

          <WrappedRoute
            path="/credit-pending"
            wrappers={[TrackedRoute, NonEntryRoute, ResetApplicationRoute]}
            component={CreditPending}
          />

          <WrappedRoute
            exact
            path="/existing-account/error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={ExistingAccountValidationError}
          />

          <WrappedRoute
            path="/existing-account"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={ExistingAccount}
          />

          <WrappedRoute
            path="/credit-error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={CreditError}
          />

          <WrappedRoute
            path="/approval"
            wrappers={[
              TrackedRoute,
              UnloadPrompt,
              NonEntryRoute,
              DspTimeoutModal,
              ResetApplicationRoute,
              MultipleAccountRoute,
            ]}
            component={Approval}
          />

          <WrappedRoute
            path="/checkout-approval"
            wrappers={[
              TrackedRoute,
              UnloadPrompt,
              NonEntryRoute,
              DspTimeoutModal,
              ResetApplicationRoute,
              MultipleAccountRoute,
            ]}
            component={CheckoutApproval}
          />

          <WrappedRoute
            path="/dsp-expired"
            wrappers={[TrackedRoute, NonEntryRoute, UnloadPrompt]}
            component={DspExpired}
          />

          <WrappedRoute
            path="/dsp-error"
            wrappers={[TrackedRoute, UnloadPrompt, NonEntryRoute]}
            component={DspError}
          />

          <WrappedRoute
            path="/unified/issuance-error"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={UnifiedIssuanceErrorAlert}
          />

          <WrappedRoute
            path="/unified/issuance-timeout"
            wrappers={[TrackedRoute, NoReturnRoute, ResetApplicationRoute]}
            component={UnifiedIssuanceTimeout}
          />

          <WrappedRoute
            path="/unified/text-me-my-offers"
            wrappers={[TrackedRoute, NoReturnRoute]}
            component={TextMeMyOfferDeadend}
          />

          <WrappedRoute
            path="/account-selection"
            wrappers={[
              TrackedRoute,
              UnloadPrompt,
              NonEntryRoute,
              DspTimeoutModal,
              ResetApplicationRoute,
            ]}
            component={AccountSelection}
          />

          <WrappedRoute
            path="/require-additional-verification"
            wrappers={[TrackedRoute, NoReturnRoute]}
            component={AdditionalVerification}
          />

          <WrappedRoute
            path="/magic-link-accept"
            wrappers={[EntryRoute, TrackedRoute]}
            component={Acceptance}
          />

          <WrappedRoute
            path="/magic_link_acceptance/error"
            wrappers={[TrackedRoute, NoReturnRoute]}
            component={AcceptanceError}
          />

          {/**
           * This was moved to the bottom of the file because `exact`
           * was removed from the wrappedRoute as it renders multiple routes.
           * When the `exact` was removed this was matching all of the routes
           * (as it matches from top down), so it needs to be the last route.
           */}
          <WrappedRoute
            path="/"
            wrappers={[EntryRoute, AppTimeoutModal, CardSelectorRoute]}
            component={Home}
          />

          {process.env.NODE_ENV === 'development' ? (
            <Route exact path="/ssr-dev-template" component={CardChoice} />
          ) : null}

          <Route
            render={({ location }) => {
              log.info(`${location.pathname} not found.`);
              return <Redirect to="/" />;
            }}
          />
        </Switch>
      </ErrorBoundary>
    </div>
  );
};

const WithProviders = nest(
  ThemeProvider,
  SnackbarProvider,
  EntryTokenProvider,
  IovationProvider,
  FmcHostProvider,
  IdiSdkProvider,
  ResponsiveQueryProvider,
  BackButtonContextProvider,
);

/**
 * The App component is nested because it depends on values provided
 * by the providers in its lifecycle.
 */
const AppWithProviders: React.FC = () => {
  return (
    <WithProviders>
      <App />
    </WithProviders>
  );
};
export default AppWithProviders;
